// Research catalog grid. Pulls the BE catalog on mount, renders cards
// in a responsive grid, and wires Add/Remove against the existing
// Application Tray (research items, kind="research"). Also owns the
// mount points for the product-detail drawer + notify-me modal so
// each card doesn't have to instantiate its own.
import { useEffect, useState } from "react";
import ResearchItemCard from "@/components/research/ResearchItemCard";
import ProductDetailDrawer from "@/components/research/ProductDetailDrawer";
import NotifyMeModal from "@/components/research/NotifyMeModal";
import { fetchResearchCatalog } from "@/lib/researchApi";
import { useApplicationTray } from "@/lib/applicationTray";
import { toast } from "@/components/Toast";
import { C } from "@/lib/brand";
import { RESEARCH_NAVY } from "@/components/research/ResearchHero";

export default function ResearchCatalog({ onLabCopy }) {
  const [items, setItems] = useState(null); // null = loading
  const [error, setError] = useState(null);
  // Filter query — applied before grouping, case-insensitive across
  // name + ingredient + sku so a physician who types "BPC" or
  // "tirzep" or "AS-TR" lands on the right cards instantly.
  const [filter, setFilter] = useState("");
  // Category chip filter. `null` = ALL (no category restriction).
  // Works in tandem with the text search: if both are active, an item
  // must match BOTH (intersection, not union).
  const [activeCategory, setActiveCategory] = useState(null);
  // Product detail drawer + notify-me modal live at this level so
  // every card can trigger a single, animated instance.
  const [drawerItem, setDrawerItem] = useState(null);
  const [notifyItem, setNotifyItem] = useState(null);
  const tray = useApplicationTray();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchResearchCatalog();
        if (cancelled) return;
        setItems(res.items);
        if (onLabCopy) onLabCopy(res.labCopy);
      } catch (err) {
        if (cancelled) return;
        // 401 on a public anonymous read means the BE is gating the
        // endpoint despite the contract — surface a graceful empty
        // state rather than the auth-detail leak. Anonymous visitors
        // still see the hero + auth strip; sign-in path is one click.
        const friendly = err?.status === 401
          ? "Sign in to view the research catalogue."
          : (err?.detail || err?.message || "We couldn't load the catalogue.");
        setError(friendly);
        setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [onLabCopy]);

  const isInTray = (sku, variant) =>
    tray.items.some((it) => it.kind === "research" && it.sku === sku && (it.variant || "subscription") === variant);

  const handleAdd = (item, variant) => {
    tray.addOrReplaceResearch({
      sku: item.sku,
      name: item.name,
      ingredient: item.ingredient,
      variant,
      quantity: 1,
      unitMonthlyPrice: item.monthlyPrice ?? null,
      unitOneTimePrice: item.oneTimePrice ?? null,
      currency: item.currency || "usd",
    });
    toast(`${item.name} added to your plan`);
  };

  const handleRemove = (item, variant) => {
    tray.removeResearch(item.sku, variant);
    toast(`${item.name} removed`);
  };

  if (items === null) {
    return (
      <section
        data-testid="research-catalog-loading"
        style={{ background: C.cream, padding: "96px 24px", textAlign: "center" }}
      >
        <div
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 11,
            letterSpacing: "0.3em",
            color: RESEARCH_NAVY,
            textTransform: "uppercase",
          }}
        >
          ▪ LOADING INVENTORY
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return (
      <section
        data-testid="research-catalog-empty"
        style={{ background: C.cream, padding: "120px 24px", textAlign: "center" }}
      >
        <div
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 11,
            letterSpacing: "0.3em",
            color: RESEARCH_NAVY,
            textTransform: "uppercase",
            marginBottom: 16,
          }}
        >
          ▪ INVENTORY UNAVAILABLE
        </div>
        <p style={{ color: C.warmGray, maxWidth: 480, margin: "0 auto", fontSize: 14, lineHeight: 1.6 }}>
          {error || "The research catalogue is being prepared. Check back shortly."}
        </p>
      </section>
    );
  }

  // Build a stable list of categories straight from the unfiltered
  // items list so chips don't disappear when the search query
  // narrows results. Ordering matches BE's sortOrder ASC.
  const allCategoriesMap = new Map();
  items.forEach((it) => {
    const slug = it.category || "_uncategorised";
    const label = it.categoryLabel || (it.category ? slugTitle(it.category) : "Other compounds");
    if (!allCategoriesMap.has(slug)) allCategoriesMap.set(slug, label);
  });
  const allCategories = Array.from(allCategoriesMap.entries()).map(([slug, label]) => ({ slug, label }));

  // Apply BOTH filters (category chip + text search) before grouping.
  // Items must match both when both are active (intersection).
  const q = filter.trim().toLowerCase();
  const filtered = items.filter((it) => {
    if (activeCategory && (it.category || "_uncategorised") !== activeCategory) return false;
    if (!q) return true;
    const haystack = [
      it.name || "",
      it.ingredient || "",
      it.sku || "",
      it.categoryLabel || "",
    ].join(" ").toLowerCase();
    return haystack.includes(q);
  });

  // Group catalogue items by category for scannability at 60+ SKUs.
  // BE returns both `category` (slug) and `categoryLabel` (display
  // string); we group by slug, render the label. Items without a
  // category fall into "OTHER COMPOUNDS" so nothing is hidden.
  //
  // BE sorts the response by sortOrder ASC then category — that gives
  // us category grouping in source order. Within each category we
  // re-sort by `name` (case-insensitive, locale-aware) so the cards
  // read alphabetically regardless of dose/SKU suffixes.
  const groups = [];
  const groupIndex = new Map(); // slug -> index in groups[]
  filtered.forEach((item) => {
    const slug = item.category || "_uncategorised";
    const label = item.categoryLabel || (item.category ? slugTitle(item.category) : "Other compounds");
    if (!groupIndex.has(slug)) {
      groupIndex.set(slug, groups.length);
      groups.push({ slug, label, items: [] });
    }
    groups[groupIndex.get(slug)].items.push(item);
  });
  // Alphabetise within each group. Use localeCompare with numeric so
  // doses sort sensibly inside a name family (e.g., "Retatrutide 5mg"
  // before "Retatrutide 10mg" before "Retatrutide 15mg" — not the
  // pure-ASCII "10, 15, 5" you'd otherwise get).
  groups.forEach((g) => {
    g.items.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base", numeric: true }),
    );
  });

  return (
    <section
      data-testid="research-catalog-grid"
      style={{ background: C.cream, padding: "72px 24px 96px" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Sticky category jump-strip. Stays pinned to the top of the
            viewport as the user scrolls the catalogue. One tap on a
            chip filters in-place; works alongside the text search
            (intersection). The strip's own background extends past
            the maxWidth wrapper via an absolutely-positioned cream
            bleed so the chips stay legible over the page content
            during sticky overlap. */}
        {allCategories.length > 1 && (
          <div
            data-testid="research-catalog-categories-strip"
            style={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              margin: "0 -24px 24px -24px",
              padding: "14px 24px",
              background: C.cream,
              borderBottom: `1px solid ${C.warmGray4}`,
              display: "flex",
              gap: 10,
              alignItems: "center",
              overflowX: "auto",
              whiteSpace: "nowrap",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <CategoryChip
              active={activeCategory === null}
              label="All"
              count={items.length}
              testId="research-catalog-chip-all"
              onClick={() => setActiveCategory(null)}
            />
            {allCategories.map((cat) => {
              const count = items.filter((it) => (it.category || "_uncategorised") === cat.slug).length;
              return (
                <CategoryChip
                  key={cat.slug}
                  active={activeCategory === cat.slug}
                  label={cat.label}
                  count={count}
                  testId={`research-catalog-chip-${cat.slug}`}
                  onClick={() => setActiveCategory(activeCategory === cat.slug ? null : cat.slug)}
                />
              );
            })}
          </div>
        )}

        {/* Search input — sticky-ish at the top of the catalogue.
            Filters by name, ingredient, SKU, or category label.
            Keeps category grouping intact: results are filtered first,
            then grouped + sorted, so matches stay organised. */}
        <div
          data-testid="research-catalog-search-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: 24,
            paddingBottom: 18,
            borderBottom: `1px solid ${C.warmGray4}`,
            position: "relative",
          }}
        >
          <span
            aria-hidden
            style={{
              fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
              fontSize: 10,
              letterSpacing: "0.3em",
              color: RESEARCH_NAVY,
              textTransform: "uppercase",
              flexShrink: 0,
            }}
          >
            ▪ FIND
          </span>
          <input
            data-testid="research-catalog-search-input"
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by compound, ingredient, or SKU…"
            spellCheck={false}
            autoComplete="off"
            style={{
              flex: 1,
              background: "transparent",
              border: 0,
              outline: "none",
              fontSize: 15,
              color: C.ink,
              padding: "6px 0",
              fontFamily: '"DM Sans", sans-serif',
              letterSpacing: "-0.005em",
            }}
          />
          {filter && (
            <button
              type="button"
              data-testid="research-catalog-search-clear"
              onClick={() => setFilter("")}
              aria-label="Clear search"
              style={{
                background: "transparent",
                border: 0,
                cursor: "pointer",
                fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
                fontSize: 10,
                letterSpacing: "0.25em",
                color: C.warmGray,
                textTransform: "uppercase",
                padding: "4px 8px",
              }}
            >
              Clear ✕
            </button>
          )}
        </div>

        <div
          data-testid="research-catalog-count"
          style={{
            fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
            fontSize: 10,
            letterSpacing: "0.3em",
            color: RESEARCH_NAVY,
            textTransform: "uppercase",
            marginBottom: 28,
          }}
        >
          {q || activeCategory ? (
            <>
              ▪ {filtered.length} OF {items.length} SHOWN
              {activeCategory && (
                <span style={{ marginLeft: 14, opacity: 0.55 }}>
                  · {allCategoriesMap.get(activeCategory) || ""}
                </span>
              )}
              {q && (
                <span style={{ marginLeft: 14, opacity: 0.55 }}>
                  · MATCH “{filter.trim()}”
                </span>
              )}
            </>
          ) : (
            <>
              ▪ {items.length} COMPOUND{items.length === 1 ? "" : "S"} AVAILABLE
              {groups.length > 1 && (
                <span style={{ marginLeft: 14, opacity: 0.55 }}>
                  · {groups.length} CATEGORIES
                </span>
              )}
            </>
          )}
        </div>

        {filtered.length === 0 && (
          <div
            data-testid="research-catalog-no-matches"
            style={{ padding: "64px 0", textAlign: "center" }}
          >
            <div
              style={{
                fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
                fontSize: 11,
                letterSpacing: "0.3em",
                color: RESEARCH_NAVY,
                textTransform: "uppercase",
                marginBottom: 12,
              }}
            >
              ▪ NO MATCHES
            </div>
            <p style={{ color: C.warmGray, fontSize: 14, margin: 0 }}>
              {q
                ? `No compound matches “${filter.trim()}”${activeCategory ? ` in ${allCategoriesMap.get(activeCategory) || ""}` : ""}.`
                : "No compounds in this category."}
            </p>
          </div>
        )}

        {groups.map((group, gi) => (
          <div
            key={group.slug}
            data-testid={`research-catalog-group-${group.slug}`}
            style={{ marginTop: gi === 0 ? 0 : 56 }}
          >
            {/* Category heading — only render when we have more than
                one group; a single-group catalogue doesn't need one. */}
            {groups.length > 1 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 14,
                  marginBottom: 22,
                  paddingBottom: 12,
                  borderBottom: `1px solid ${C.warmGray4}`,
                }}
              >
                <h2
                  data-testid={`research-catalog-group-label-${group.slug}`}
                  style={{
                    margin: 0,
                    fontSize: 18,
                    fontWeight: 800,
                    letterSpacing: "-0.01em",
                    color: RESEARCH_NAVY,
                    textTransform: "uppercase",
                    fontFamily: '"DM Sans", sans-serif',
                  }}
                >
                  {group.label}
                </h2>
                <span
                  style={{
                    fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
                    fontSize: 10,
                    letterSpacing: "0.25em",
                    color: C.warmGray,
                  }}
                >
                  ▪ {group.items.length}
                </span>
              </div>
            )}

            <div
              className="grid-cols-1 md:!grid-cols-2 lg:!grid-cols-3"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 20,
              }}
            >
              {group.items.map((item) => {
                const defaultVariant = item.monthlyPrice != null ? "subscription" : "one_time";
                const inTraySub = isInTray(item.sku, "subscription");
                const inTrayOne = isInTray(item.sku, "one_time");
                const inTrayDefault = isInTray(item.sku, defaultVariant);
                return (
                  <ResearchItemCard
                    key={item.sku}
                    item={item}
                    isInTray={inTraySub || inTrayOne || inTrayDefault}
                    onAdd={handleAdd}
                    onRemove={handleRemove}
                    onLearnMore={setDrawerItem}
                    onNotify={setNotifyItem}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Detail drawer + notify modal — single instance each; item
          becomes null when closed so animations exit cleanly. */}
      <ProductDetailDrawer
        item={drawerItem}
        isOpen={!!drawerItem}
        onClose={() => setDrawerItem(null)}
        onAdd={handleAdd}
        onNotify={(it) => { setDrawerItem(null); setNotifyItem(it); }}
        isInTray={drawerItem ? (
          isInTray(drawerItem.sku, "subscription")
          || isInTray(drawerItem.sku, "one_time")
          || isInTray(drawerItem.sku, drawerItem.monthlyPrice != null ? "subscription" : "one_time")
        ) : false}
      />
      <NotifyMeModal
        item={notifyItem}
        isOpen={!!notifyItem}
        onClose={() => setNotifyItem(null)}
      />
    </section>
  );
}

function slugTitle(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((s) => s[0].toUpperCase() + s.slice(1))
    .join(" ");
}

// Single chip in the sticky category jump-strip. Mono-styled, navy
// when active (high contrast), warm-gray default state. The trailing
// numeric badge is the total count for that category in the
// unfiltered source — gives the physician a sense of catalogue depth
// at a glance without needing to tap through.
function CategoryChip({ active, label, count, testId, onClick }) {
  const NAVY = "#1F3349";
  const NAVY_DEEP = "#0F1B2A";
  return (
    <button
      type="button"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      style={{
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "8px 14px",
        border: `1px solid ${active ? NAVY_DEEP : C.warmGray3}`,
        background: active ? NAVY_DEEP : "transparent",
        color: active ? C.cream : NAVY,
        fontFamily: '"JetBrains Mono", "IBM Plex Mono", ui-monospace, monospace',
        fontSize: 10,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        fontWeight: 800,
        cursor: "pointer",
        transition: "background 140ms ease, color 140ms ease, border-color 140ms ease",
      }}
    >
      <span>{label}</span>
      <span
        style={{
          fontSize: 9,
          letterSpacing: "0.12em",
          opacity: active ? 0.7 : 0.55,
          fontWeight: 600,
        }}
      >
        {count}
      </span>
    </button>
  );
}
